import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { useAnalytics } from 'use-analytics';
import { addToBrochure } from '../store/slices/brochure';
import { TravelTypes } from '../constants';
import useBrochureOptionRemoval from './useBrochureOptionRemoval';
import getBrochureTripId from '../helpers/getBrochureTripId';

const useHandleHotelAddToBrochure = ({
  tripId,
  hotelItinerary,
  product,
  brochure,
  isViewingBrochure,
  isBrochurePage,
  currentBrochure,
  brochureOnBrochurePage,
  tripRoomSearchInput,
}) => {
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const { staterooms } = useSelector((store) => store.tripSearch.searchObj);
  const handleSegmentRemoval = useBrochureOptionRemoval();
  const findHotelItineraryByKeyAndDates = (tripSegments, hotelKey) =>
    _.find(
      tripSegments,
      (ts) =>
        ts.itinerary.hotelKey === hotelKey &&
        ts.itinerary?.startDateTime === hotelItinerary?.startDateTime &&
        ts.itinerary?.endDateTime === hotelItinerary?.endDateTime,
    );

  const findHotelRoom = (products, productCode) => _.find(products, (i) => i.code === productCode);

  const handleAddToBrochureClick = (event) => {
    if (event && event.type === 'keydown' && event.key !== 'Enter') return;

    if (!hotelItinerary || !product) {
      return;
    }
    const { hotelKey } = hotelItinerary;
    const id = getBrochureTripId(
      tripId,
      brochure,
      hotelItinerary,
      tripRoomSearchInput || staterooms,
      TravelTypes.hotel,
    );

    const handleSegmentRemovalWrapper = (brochureId, brochureToUpdate, isSavedToDatabase) => {
      handleSegmentRemoval({
        brochureId,
        currentBrochure: brochureToUpdate,
        travelType: TravelTypes.hotel,
        segmentKey: 'hotelKey',
        segmentKeyValue: hotelKey,
        findTripSegmentfn: findHotelItineraryByKeyAndDates,
        isSavedToDatabase,
        product,
        findHotelRoomfn: findHotelRoom,
        itinerary: hotelItinerary,
        staterooms: tripRoomSearchInput,
      });
    };

    if (isBrochurePage && brochureOnBrochurePage) {
      const brochureToUpdate = JSON.parse(JSON.stringify(brochureOnBrochurePage));
      handleSegmentRemovalWrapper(tripId, brochureToUpdate, true);
      return;
    }

    if (isViewingBrochure && currentBrochure) {
      handleSegmentRemovalWrapper(brochure?.id, currentBrochure, brochure?.isSavedToDatabase);
    }

    let updatedTrip = { tripSegments: [] };
    if (brochure?.trips?.[id]) {
      updatedTrip = _.cloneDeep(brochure.trips[id]);
    }

    let action = 'add';
    const hotelTripSeg = findHotelItineraryByKeyAndDates(updatedTrip.tripSegments, hotelKey);
    if (!hotelTripSeg) {
      updatedTrip.tripSegments.push({
        itinerary: {
          ...hotelItinerary,
          hotel: {
            ...hotelItinerary.hotel,
            products: [{ ...product }],
          },
        },
      });
    } else {
      const matchedProduct = findHotelRoom(hotelTripSeg.itinerary.hotel.products, product.code);
      if (matchedProduct) {
        _.remove(hotelTripSeg.itinerary.hotel.products, (p) => p.code === product.code);
        action = 'update_remove';
      } else {
        hotelTripSeg.itinerary.hotel.products.push(product);
        hotelTripSeg.itinerary.hotel.products = _.orderBy(
          hotelTripSeg.itinerary.hotel.products,
          'roomPrice.markupPrice',
        );
        action = 'update_add';
      }

      if (hotelTripSeg.itinerary.hotel.products.length === 0) {
        _.remove(updatedTrip.tripSegments, (ts) => ts.itinerary.hotelKey === hotelKey);
        action = 'remove';
      }
    }
    dispatch(addToBrochure({ tripId: id, updatedTrip, staterooms }));
    track(`HotelBrochure.${action}`, { hotelKey, productCode: product?.code });
  };

  return handleAddToBrochureClick;
};

export default useHandleHotelAddToBrochure;
